
import {
    computed,
    defineComponent,
    onBeforeMount,
    onBeforeUnmount,
    PropType,
    reactive,
    ref,
} from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// module components
import CourierSchedule from '@/modules/CouriersModule/components/CourierSchedule/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { decimal, required, requiredIf } from '@vuelidate/validators'

// types
import { CouirerCarT, UpdateCourierPayloadT, DispatcherCourierT, CourierTypeT } from '@/core/types/couriers.types'
import { DispatcherSectorT } from '@/core/types/dispatchers_sector.types'
import { CarrierT } from '@/core/types/carriers.types'

import {
    PersonDataResponseT,
    InProgressDataResponseT,
    InvalidDataResponseT,
    SecurityGetPersonInformationResponseT,
} from '@/core/types/security.types'

// constants
import {
    CourierTypes,
    CourierCarTypes,
    CourierPaymentTypes,
    CourierStatuses,
    COURIER_DEFAULT_GRAPH,
} from '@/core/constants/couriers.constants'
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisation
import { dispatchers_sectors } from '@/core/realisations/dispatchers_sectors.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'
import { security } from '@/core/realisations/security.realisation'

// composable
import { useMaska } from '@/common/composable/useMaska'
import useNotifications from '@/common/composable/useNotifications'

const PERSON_CHECKUP_TYPE = 2
const TIMER_INTERVAL = 7 * 1000 // 7s

export default defineComponent({
    components: {
        PageTitle,
        TheInput,
        TheSelect,
        TheButton,
        FileReader,
        CourierSchedule,
    },
    props: {
        isCreate: {
            type: Boolean,
            default: true,
        },
        isUpdate: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        initialValue: {
            type: Object as PropType<DispatcherCourierT>,
            default: null,
        },
    },
    emits: ['cancel', 'create', 'update'],
    setup(props, { emit }) {
        const { unmaskNumbers } = useMaska()
        const notifications = useNotifications()

        const step = ref(1)

        const state = reactive({
            id: undefined as number | undefined,
            dispatchers_sector_id: null as number | null,
            company_id: null as number | null,
            iin: null as string | null,
            full_name: null as string | null,
            phone_number: null as string | null,
            password: null as string | null,
            payment_rate: null as number | null,
            payment_amount: null as string | null,
            car_id: null as number | null,
            code_1c: null as string | null,
            is_active: true,
            is_foreign: false,
            certificate: [] as File[],
            driver_license: [] as File[],
            courierLicenses: {
                identifyCardNumber: null as string | null,
                identifyCardIssueDate: null as string | null,
                driverLicenseNumber: null as string | null,
                driverLicenseIssueDate: null as string | null,
            },
            typeIds: [] as number[],
            schedules: [...COURIER_DEFAULT_GRAPH],
        })

        let CourierCreateOrUpdateValidation = {
            dispatchers_sector_id: { required },
            courierLicenses: {
                identifyCardNumber: { required },
                identifyCardIssueDate: { required },
                driverLicenseNumber: { required },
                driverLicenseIssueDate: { required },
            },
            company_id: {
                required: requiredIf(() => {
                    return !props.initialValue
                }),
            },
            iin: { required },
            full_name: { required },
            phone_number: { required },
            password: {
                required: requiredIf(() => {
                    return !props.isUpdate
                }),
            },
            payment_rate: { required },
            payment_amount: { required, decimal },
            car_id: { required },
            typeIds: { required }
        }

        const state_form$ = useVuelidate(CourierCreateOrUpdateValidation, state)

        const sb_state = reactive({
            is_checked: false,
            is_already_created: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
        })

        const resetSbState = () => {
            sb_state.is_checked = false
            sb_state.is_already_created = false
            sb_state.is_rejected = null
            sb_state.reject_comment = null
            sb_state.is_request_completed = false

            state.is_foreign = false
        }

        const person_data = reactive({
            is_loading: false,
            is_information_loading: false,
            is_not_found: false,
            is_in_progress: false,
            not_found_message: null as string | null,
            update_timer: null as number | null,
            is_loaded: false,
        })

        const createPersonUpdateTimer = () => {
            // @ts-ignore
            person_data.update_timer = setInterval(getPersonInformation, TIMER_INTERVAL)
        }

        const removePersonUpdateTimer = () => {
            // @ts-ignore
            clearInterval(person_data.update_timer)
            person_data.update_timer = null
        }

        // const isPersonNotFound = (response: SecurityGetPersonInformationResponseT): boolean => {
        //     const invalid_response = response as InvalidDataResponseT

        //     const key = invalid_response.key
        //     const message = invalid_response.message

        //     if (key && key === 'not_found') {
        //         person_data.not_found_message = message
        //         return true
        //     }

        //     person_data.not_found_message = null
        //     return false
        // }

        // const isPersonInProgress = (response: SecurityGetPersonInformationResponseT): boolean => {
        //     const key = (response as InProgressDataResponseT).key

        //     if (key && key === 'in_progress') return true
        //     return false
        // }

        const getPersonInformation = async () => {
            person_data.is_loaded = false

            try {
                person_data.is_information_loading = true

                // const person_iin = state.iin as string
                // const response = await security.getPersonInformation(person_iin)

                // person_data.is_not_found = isPersonNotFound(response)
                // person_data.is_in_progress = isPersonInProgress(response)

                if (person_data.is_not_found) {
                    removePersonUpdateTimer()
                    return
                }

                if (person_data.is_in_progress) {
                    if (!person_data.update_timer) {
                        createPersonUpdateTimer()
                    }
                    return
                }

                removePersonUpdateTimer()

                loadDataState()
                // const person_information = (response as PersonDataResponseT).data

                // АВТОЗАПОЛНЕНИЕ ADATA
                // state.full_name = person_information.name

                step.value = 2
                person_data.is_loaded = true
            } catch (error: any) {
                if (error.status === 500) {
                    state.is_foreign = true
                    step.value = 2
                    loadDataState()
                    person_data.is_loaded = true
                }

                notifications.notifyError({
                    error,
                    title: 'При получении информации о физ. лице произошла ошибка',
                })
            } finally {
                person_data.is_information_loading = false
            }
        }

        // const createPersonInformationRequest = async () => {
        //     try {
        //         person_data.is_loading = true

        //         const person_iin = state.iin as string
        //         await security.createPersonInformationRequest(person_iin)

        //         getPersonInformation()
        //     } catch (error) {
        //         notifications.notifyError({
        //             error,
        //             title: 'При создании запроса на получение информации о физ. лице произошла ошибка',
        //         })
        //     } finally {
        //         person_data.is_loading = false
        //     }
        // }

        const checkSbStatus = async () => {
            if (sb_state.is_loading || person_data.is_loading || person_data.is_information_loading)
                return
            resetSbState()

            try {
                sb_state.is_loading = true

                const response: any = await security.getCheckupStatus({
                    identifier: state.iin as string,
                    type: PERSON_CHECKUP_TYPE,
                    checkInSystem: 1,
                })

                if (!response.success) {
                    if (response.code === 400) {
                        sb_state.is_already_created = true
                    }

                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                }

                sb_state.is_checked = true

                if (response.data.isAccepted === false) {
                    sb_state.is_rejected = true
                    sb_state.reject_comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    getPersonInformation()
                    // createPersonInformationRequest()
                }
            } catch (error: any) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state.is_checked = false
            } finally {
                sb_state.is_loading = false
                sb_state.is_request_completed = true
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitManager = () => {
            if (step.value === 1) {
                checkSbStatus()
                return
            }

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (props.isCreate) {
                const form_data = new FormData()

                form_data.append('fullName', state.full_name as string)
                form_data.append('phoneNumber', unmaskNumbers(state.phone_number as string))
                form_data.append('password', state.password as string)
                form_data.append(
                    'dispatcherSectorId',
                    (state.dispatchers_sector_id as number).toString()
                )
                form_data.append('carId', (state.car_id as number).toString())
                form_data.append('isActive', '1')
                form_data.append('paymentRateType', (state.payment_rate as number).toString())
                form_data.append('paymentAmount', state.payment_amount as string)
                form_data.append('companyId', (state.company_id as number).toString())
                form_data.append('code1c', state.code_1c ? state.code_1c : '')
                form_data.append('iin', state.iin ? state.iin : '')
                form_data.append('courierLicenses', JSON.stringify(state.courierLicenses))
                form_data.append('typeIds', JSON.stringify(state.typeIds))

                if (state.certificate.length) {
                    state.certificate.forEach((certificate) => {
                        form_data.append('identificationCards[]', certificate)
                    })
                }

                if (state.driver_license.length) {
                    state.driver_license.forEach((driver_license) => {
                        form_data.append('driverLicenses[]', driver_license)
                    })
                }

                emit('create', form_data, [...state.schedules])
                return
            }

            if (props.isUpdate) {
                const payload: UpdateCourierPayloadT = {
                    fullName: state.full_name as string,
                    phoneNumber: unmaskNumbers(state.phone_number as string),
                    password: state.password as string,
                    dispatcherSectorId: state.dispatchers_sector_id as number,
                    carId: state.car_id as number,
                    paymentRateType: state.payment_rate as number,
                    paymentAmount: state.payment_amount as string,
                    companyId: state.company_id as number,
                    code1C: state.code_1c,
                    iin: state.iin as string,
                    courierLicenses: { ...state.courierLicenses },
                    typeIds: state.typeIds
                }

                emit('update', { id: props.initialValue.id, payload })
            }
        }

        const data_state = reactive({
            dispatchers_sectors: new DataState<DispatcherSectorT>({}),
            companies: new DataState<CarrierT>({}),
            cars: new DataState<CouirerCarT>({}),
            types: new DataState<CourierTypeT>({})
        })

        const loadDispatchersSectors = async () => {
            try {
                data_state.dispatchers_sectors.is_loading = true

                const response = await dispatchers_sectors.getList()
                data_state.dispatchers_sectors.list = response.data

                data_state.dispatchers_sectors.is_failed = false
                data_state.dispatchers_sectors.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.dispatchers_sectors.failure.catchError(error)

                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_failed = true
                }
            } finally {
                if (!data_state.dispatchers_sectors.failure.error_state.is_cancel) {
                    data_state.dispatchers_sectors.is_loading = false
                }
            }
        }

        const loadCourierTypes = async () => {
            try {
                data_state.types.is_loading = true

                const response = await couriers.getCourierTypes()
                data_state.types.list = response.data

                data_state.types.is_failed = false
                data_state.types.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.types.failure.catchError(error)

                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_failed = true
                }
            } finally {
                if (!data_state.types.failure.error_state.is_cancel) {
                    data_state.types.is_loading = false
                }
            }
        }

        const loadCarriers = async (page: number, search?: string) => {
            data_state.companies.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.companies.is_loading = true

                const response = await carriers.getList(params)

                data_state.companies.list = response.data
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.companies.pagination.total_pages) return
            data_state.companies.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.companies.is_loading = true

                const response = await carriers.getList(params)
                data_state.companies.list = data_state.companies.list.concat(response.data)
                data_state.companies.pagination.total_pages = response.meta.last_page

                data_state.companies.is_failed = false
                data_state.companies.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.companies.failure.catchError(error)

                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_failed = true
                }
            } finally {
                if (!data_state.companies.failure.error_state.is_cancel) {
                    data_state.companies.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            // data_state.companies.pagination.current_page
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (company: CarrierT) => {
            await loadCarriers(data_state.companies.pagination.current_page)
            data_state.companies.list.unshift(company)
        }

        const loadCars = async (page: number, search: string | null = null) => {
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    number: search,
                    //
                    companyId: null,
                    ftlCarTypeId: null,
                    statusId: null,
                })

                data_state.cars.list = response.data
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cars.failure.catchError(error)

                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_failed = true
                }
            } finally {
                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_loading = false
                }
            }
        }

        const loadMoreCars = async (page: number) => {
            if (page > data_state.cars.pagination.total_pages) return
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    //
                    number: null,
                    companyId: null,
                    ftlCarTypeId: null,
                    statusId: null,
                })
                data_state.cars.list = data_state.cars.list.concat(response.data)
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cars.failure.catchError(error)

                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_failed = true
                }
            } finally {
                if (!data_state.cars.failure.error_state.is_cancel) {
                    data_state.cars.is_loading = false
                }
            }
        }

        const searchCars = (search_query: string) => {
            loadCars(1, search_query)
        }

        const unshiftFindedCars = async (car: CouirerCarT) => {
            await loadCars(data_state.cars.pagination.current_page)
            data_state.cars.list.unshift(car)
        }

        const loadDataState = () => {
            loadDispatchersSectors()
            loadCarriers(1)
            loadCars(1)
            loadCourierTypes()
        }

        const onScheduleRemove = (weekday: number) => {
            const index = state.schedules.findIndex((schedule) => schedule.weekday === weekday)
            if (index < 0) return
            state.schedules.splice(index, 1)
        }

        const onScheduleAdd = (weekday: number) => {
            state.schedules.push({
                ...COURIER_DEFAULT_GRAPH[0],
                weekday,
            })
        }

        onBeforeMount(() => {
            loadDataState()

            if (!props.initialValue) return
            step.value = 2

            state.id = props.initialValue.id

            if (props.initialValue.dispatcherSector) {
                state.dispatchers_sector_id = props.initialValue.dispatcherSector.id
            }
            
            state.company_id = props.initialValue.company.id
            state.iin = props.initialValue.iin
            state.full_name = props.initialValue.fullName
            state.phone_number = props.initialValue.phoneNumber
            state.password = null
            state.code_1c = props.initialValue.code1C
            state.payment_amount = props.initialValue.paymentAmount
            state.is_active = Boolean(props.initialValue.isActive)
            state.typeIds = props.initialValue.types.map((type) => type.id)

            if (props.initialValue.paymentRateType) {
                state.payment_rate = props.initialValue.paymentRateType.id
            }

            if (props.initialValue.license) {
                state.courierLicenses.identifyCardNumber = props.initialValue.license.identifyCardNumber
                state.courierLicenses.identifyCardIssueDate = props.initialValue.license.identifyCardIssueDate
                state.courierLicenses.driverLicenseNumber = props.initialValue.license.driverLicenseNumber
                state.courierLicenses.driverLicenseIssueDate = props.initialValue.license.driverLicenseIssueDate
            }

            if (props.initialValue.car) {
                state.car_id = props.initialValue.car.id
            }
        })

        const isNotFound = computed(
            () =>
                step.value === 1 &&
                sb_state.is_rejected === null &&
                sb_state.is_request_completed &&
                !state.is_foreign
        )
        const isRejected = computed(
            () => step.value === 1 && sb_state.is_rejected && sb_state.is_request_completed
        )
        const isNotChecked = computed(
            () => step.value === 1 && !sb_state.is_checked && sb_state.is_request_completed
        )
        const isIINDisabled = computed(
            () =>
                sb_state.is_loading ||
                person_data.is_loaded ||
                (props.isUpdate && props.initialValue && props.initialValue.iin)
        )
        const isLoading = computed(() => {
            return (
                sb_state.is_loading || person_data.is_loading || person_data.is_information_loading
            )
        })

        onBeforeUnmount(removePersonUpdateTimer)

        return {
            person_data,
            step,
            resetSbState,
            sb_state,
            CourierTypes,
            CourierCarTypes,
            CourierPaymentTypes,
            CourierStatuses,
            state,
            state_form$,
            data_state,
            loadDispatchersSectors,
            loadCourierTypes,
            loadCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            loadMoreCarriers,
            emitCancel,
            emitManager,
            PHONE_NUMBER_MASK,
            loadCars,
            loadMoreCars,
            searchCars,
            unshiftFindedCars,
            onScheduleRemove,
            onScheduleAdd,
            //
            isRejected,
            isNotChecked,
            isNotFound,
            isIINDisabled,
            isLoading,
        }
    },
})
